/**
 * QMS Attachments Component
 * Displays, uploads, and manages file attachments for a QMS ticket
 * Includes image preview functionality
 */

import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Paperclip, Upload, Download, Trash2, FileText, Image, File, Loader2, X, ZoomIn } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useQmsAttachments,
  useUploadQmsAttachment,
  useDeleteQmsAttachment,
  qmsAttachmentKeys,
} from '../../hooks/useQmsAttachments';
import { getQmsAttachmentDownloadUrl } from '../../api/qmsApi';
import { formatDistanceToNow } from 'date-fns';
import type { QmsAttachment } from '../../types/qms.types';

interface QmsAttachmentsProps {
  ticketId: string;
}


function isImageMimeType(mimeType?: string): boolean {
  return !!mimeType?.startsWith('image/');
}

function getFileIcon(mimeType?: string) {
  if (isImageMimeType(mimeType)) {
    return <Image className="h-4 w-4 text-blue-500" />;
  }
  if (mimeType?.includes('pdf')) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  return <File className="h-4 w-4 text-gray-500" />;
}

function isOptimisticAttachment(attachment: QmsAttachment): boolean {
  return attachment.id.startsWith('temp-');
}

/**
 * Image preview component with loading state and error handling
 */
function ImagePreview({
  src,
  alt,
  onClick,
  className = '',
}: {
  src: string;
  alt: string;
  onClick?: () => void;
  className?: string;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Image className="h-6 w-6 text-gray-400" />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover cursor-pointer transition-opacity ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
        onClick={onClick}
      />
      {!isLoading && !hasError && onClick && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors cursor-pointer group"
          onClick={onClick}
        >
          <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}
    </div>
  );
}

/**
 * Lightbox dialog for viewing full-size images
 */
function ImageLightbox({
  isOpen,
  onClose,
  imageUrl,
  imageName,
}: {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageName: string;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-black/95">
        <div className="relative w-full h-full flex items-center justify-center">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
          <img
            src={imageUrl}
            alt={imageName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white text-sm truncate">{imageName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Attachment item - shows preview for images, icon for other files
 */
function AttachmentItem({
  attachment,
  ticketId,
  onDelete,
  isDeleting,
  onImageClick,
}: {
  attachment: QmsAttachment;
  ticketId: string;
  onDelete: (attachmentId: string) => void;
  isDeleting: boolean;
  onImageClick: (attachment: QmsAttachment) => void;
}) {
  const downloadUrl = getQmsAttachmentDownloadUrl(ticketId, attachment.id);
  const isImage = isImageMimeType(attachment.mimeType);
  const isOptimistic = isOptimisticAttachment(attachment);

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Thumbnail or Icon */}
        {isOptimistic ? (
          <div className="flex-shrink-0 w-12 h-12 rounded-md border bg-gray-50 flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : isImage ? (
          <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border bg-gray-100">
            <ImagePreview
              src={downloadUrl}
              alt={attachment.originalFilename}
              onClick={() => onImageClick(attachment)}
              className="w-12 h-12"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-12 h-12 rounded-md border bg-gray-50 flex items-center justify-center">
            {getFileIcon(attachment.mimeType)}
          </div>
        )}

        {/* File info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{attachment.originalFilename}</p>
          <p className="text-xs text-gray-500">
            {isOptimistic ? 'Uploading...' : attachment.sizeBytes ? formatBytes(attachment.sizeBytes) : ''}
            {attachment.uploadedByName && ` • ${attachment.uploadedByName}`}
            {attachment.createdAt &&
              ` • ${formatDistanceToNow(new Date(attachment.createdAt), { addSuffix: true })}`}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 ml-2">
        {!isOptimistic && (
          <>
            {isImage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onImageClick(attachment)}
                aria-label={`Preview ${attachment.originalFilename}`}
              >
                <ZoomIn className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="h-8 w-8"
              aria-label={`Download ${attachment.originalFilename}`}
            >
              <a href={downloadUrl} download={attachment.originalFilename} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => onDelete(attachment.id)}
              disabled={isDeleting}
              aria-label={`Delete ${attachment.originalFilename}`}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Image gallery grid for displaying image attachments
 */
function ImageGallery({
  images,
  ticketId,
  onImageClick,
  onDelete,
  deletingId,
}: {
  images: QmsAttachment[];
  ticketId: string;
  onImageClick: (attachment: QmsAttachment) => void;
  onDelete: (attachmentId: string) => void;
  deletingId?: string;
}) {
  if (images.length === 0) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {images.map((attachment) => {
        const downloadUrl = getQmsAttachmentDownloadUrl(ticketId, attachment.id);
        const isDeleting = deletingId === attachment.id;
        const isOptimistic = isOptimisticAttachment(attachment);

        return (
          <div
            key={attachment.id}
            className="relative group aspect-square rounded-lg overflow-hidden border bg-gray-100"
          >
            {isOptimistic ? (
              <div className="flex h-full w-full items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <ImagePreview
                src={downloadUrl}
                alt={attachment.originalFilename}
                onClick={() => onImageClick(attachment)}
                className="w-full h-full"
              />
            )}

            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <p className="text-white text-xs truncate mb-1">{attachment.originalFilename}</p>
                {isOptimistic ? (
                  <p className="text-white text-xs">Uploading...</p>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-white hover:bg-white/20"
                      onClick={() => onImageClick(attachment)}
                    >
                      <ZoomIn className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      asChild
                      className="h-7 w-7 text-white hover:bg-white/20"
                    >
                      <a href={downloadUrl} download={attachment.originalFilename} target="_blank" rel="noopener noreferrer">
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-white hover:bg-red-500/50"
                      onClick={() => onDelete(attachment.id)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function QmsAttachments({ ticketId }: QmsAttachmentsProps) {
  const { t } = useTranslation('qms');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { data: attachments = [], isLoading } = useQmsAttachments(ticketId);
  const uploadAttachment = useUploadQmsAttachment();
  const deleteAttachment = useDeleteQmsAttachment();

  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState<QmsAttachment | null>(null);

  // Separate images from other files
  const imageAttachments = attachments.filter((a) => isImageMimeType(a.mimeType));
  const otherAttachments = attachments.filter((a) => !isImageMimeType(a.mimeType));

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Copy files to array BEFORE clearing the input (clearing input clears FileList)
      const fileArray = Array.from(files);

      // Clear the input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Upload in parallel with a small concurrency cap so the browser's
      // HTTP/1.1 connection pool isn't saturated and other requests on the
      // page (image previews, session pings) don't starve.
      const MAX_CONCURRENT_UPLOADS = 3;
      let successCount = 0;
      const errors: string[] = [];
      let cursor = 0;
      const batchStart = performance.now();
      const totalBytes = fileArray.reduce((sum, f) => sum + f.size, 0);
      console.log(
        `[qms-upload] batch start — ${fileArray.length} file(s), ${(totalBytes / 1024 / 1024).toFixed(2)} MB total, concurrency=${Math.min(MAX_CONCURRENT_UPLOADS, fileArray.length)}`,
      );
      const runWorker = async () => {
        while (cursor < fileArray.length) {
          const index = cursor++;
          const file = fileArray[index];
          const fileStart = performance.now();
          try {
            await uploadAttachment.mutateAsync({ ticketId, file });
            const elapsed = performance.now() - fileStart;
            const mb = file.size / 1024 / 1024;
            const mbps = mb / (elapsed / 1000);
            console.log(
              `[qms-upload] ✓ ${file.name} (${mb.toFixed(2)} MB) — ${elapsed.toFixed(0)} ms (${mbps.toFixed(2)} MB/s)`,
            );
            successCount++;
          } catch (error) {
            const elapsed = performance.now() - fileStart;
            console.log(`[qms-upload] ✗ ${file.name} — failed after ${elapsed.toFixed(0)} ms`);
            const message = error instanceof Error ? error.message : 'Upload failed';
            errors.push(`${file.name}: ${message}`);
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(MAX_CONCURRENT_UPLOADS, fileArray.length) }, runWorker),
      );
      const batchElapsed = performance.now() - batchStart;
      const totalMb = totalBytes / 1024 / 1024;
      const throughput = totalMb / (batchElapsed / 1000);
      console.log(
        `[qms-upload] batch done — ${successCount}/${fileArray.length} succeeded in ${batchElapsed.toFixed(0)} ms (${throughput.toFixed(2)} MB/s aggregate)`,
      );

      // Single invalidation after the batch — replaces N concurrent refetches
      // (one per upload) with one, and reconciles optimistic temp entries
      // with the server's canonical records.
      queryClient.invalidateQueries({ queryKey: qmsAttachmentKeys.all(ticketId) });

      // Show appropriate toast
      if (successCount > 0 && errors.length === 0) {
        toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`);
      } else if (errors.length > 0) {
        if (successCount > 0) {
          toast.warning(`${successCount} uploaded, ${errors.length} failed`);
        }
        errors.forEach((err) => toast.error(err));
      }
    },
    [ticketId, uploadAttachment, queryClient]
  );

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      try {
        await deleteAttachment.mutateAsync({ ticketId, attachmentId });
        toast.success('Attachment deleted');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete attachment';
        toast.error(message);
      }
    },
    [ticketId, deleteAttachment]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageClick = (attachment: QmsAttachment) => {
    setLightboxImage(attachment);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="h-5 w-5" />
          {t('attachments.title', 'Attachments')}
        </h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="h-5 w-5" />
          {t('attachments.title', 'Attachments')}
          <span className="text-sm font-normal text-gray-500">
            ({attachments.length})
          </span>
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={handleUploadClick}
          disabled={uploadAttachment.isPending}
        >
          {uploadAttachment.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          {t('attachments.upload', 'Upload File')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
        />
      </div>

      {/* Attachments Display */}
      {attachments.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
          <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('attachments.empty', 'No attachments yet')}</p>
          <p className="text-xs">{t('attachments.emptyHint', 'Upload files to attach them to this ticket')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Image Gallery */}
          {imageAttachments.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t('attachments.images', 'Images')} ({imageAttachments.length})
              </p>
              <ImageGallery
                images={imageAttachments}
                ticketId={ticketId}
                onImageClick={handleImageClick}
                onDelete={handleDelete}
                deletingId={deleteAttachment.isPending ? deleteAttachment.variables?.attachmentId : undefined}
              />
            </div>
          )}

          {/* Other Files List */}
          {otherAttachments.length > 0 && (
            <div className="space-y-2">
              {imageAttachments.length > 0 && (
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('attachments.files', 'Files')} ({otherAttachments.length})
                </p>
              )}
              <div className="space-y-2">
                {otherAttachments.map((attachment) => (
                  <AttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    ticketId={ticketId}
                    onDelete={handleDelete}
                    isDeleting={
                      deleteAttachment.isPending &&
                      deleteAttachment.variables?.attachmentId === attachment.id
                    }
                    onImageClick={handleImageClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          isOpen={!!lightboxImage}
          onClose={() => setLightboxImage(null)}
          imageUrl={getQmsAttachmentDownloadUrl(ticketId, lightboxImage.id)}
          imageName={lightboxImage.originalFilename}
        />
      )}
    </div>
  );
}
